/**
 * SimAccidentsTimeline — таймлайн слоя «ДТП СИМ» (DA-15040, диапазон — DA-14953).
 *
 * Показывается в двух режимах периода:
 *  - 'date'  — курсор на конкретную дату; в шапке степпер года (‹ 2025 ›), рельса скоупится
 *              на выбранный год (границы = min/max дата инцидентов этого года).
 *  - 'range' — выбор диапазона дат (два бегунка, range-brush MapTimeline); рельса охватывает
 *              весь доступный период (все годы), без скоупа по году.
 *
 * Источник дат — MVT-слой 'metadata' функции martin.sim_accidents_mvt (свойства min_date/
 * max_date/dates_json), читается через queryRenderedFeatures (точка metadata лежит в углу
 * каждого тайла, как у geo_starts). Курсор/диапазон и год пишутся в mapFilters; применение
 * фильтра к карте — Map.applySimAccidentsStyle.
 */
import React from 'react';
import { connect } from 'react-redux';
import { onChangeMapFilters, onChangeMapFiltersMulti } from '../../ac/onChangeMapFilters';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { MapTimeline } from './MapTimeline';
import { t } from '@lingui/macro';

const SOURCE_ID = 'martin.sim_accidents_mvt';
const TICK_COLOR = '#e53935';
// Высота штриха таймлайна (px): базовая = CSS-дефолт 9px; диапазон ½×…2×.
const TICK_BASE_PX = 9;
const TICK_MIN_PX = 4.5;
const TICK_MAX_PX = 18;
const MS_DAY = 86400000;
// ISO 'YYYY-MM-DD' → номер дня (UTC), и обратно. Целочисленный домен дней для рельсы.
const dayNum = iso => {
    if (!iso) return null;
    const [ y, m, d ] = iso.split('-').map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / MS_DAY);
};
const isoFromDay = n => new Date(n * MS_DAY).toISOString().slice(0, 10);
const fmtDDMM = iso => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
// Чтение metadata-слоя (min_date/max_date/dates_json) через невидимый query-слой.
const readMeta = map => {
    if (!map?.getSource(SOURCE_ID)) return null;
    const qid = '__sim_accidents_meta_query';
    if (!map.getLayer(qid)) {
        try {
            map.addLayer({ id: qid, type: 'fill', source: SOURCE_ID, 'source-layer': 'metadata', paint: { 'fill-opacity': 0 } });
        } catch (e) { return null }
    }
    let rendered = [];
    try { rendered = map.queryRenderedFeatures({ layers: [ qid ] }) } catch (e) { return null }
    if (!rendered.length) return null;
    const p = rendered[0].properties || {};
    let dates = [];
    try { dates = JSON.parse(p.dates_json || '[]') } catch (e) { dates = [] }
    return { minDate: p.min_date, maxDate: p.max_date, dates: Array.isArray(dates) ? dates : [] };
};
const SimAccidentsTimelineComponent = ({
    simAccidentsPeriodMode, simAccidentsDate, simAccidentsYear,
    simAccidentsRangeFrom, simAccidentsRangeTo,
    setFilter, setFilterMulti,
}) => {
    // eslint-disable-next-line no-underscore-dangle
    const map = typeof window !== 'undefined' ? window.__map : null;
    const isTimelineMode = simAccidentsPeriodMode === 'date' || simAccidentsPeriodMode === 'range';
    const [ meta, setMeta ] = React.useState(null);

    // Подгружаем metadata, когда тайлы загружены.
    React.useEffect(() => {
        if (!map || !isTimelineMode) return () => {};
        const load = () => { const m = readMeta(map); if (m && m.dates.length) setMeta(m); };
        const onSourceData = e => { if (e.sourceId === SOURCE_ID && e.isSourceLoaded) load(); };
        const tid = setTimeout(load, 400);
        map.on('sourcedata', onSourceData);
        return () => { clearTimeout(tid); map.off('sourcedata', onSourceData) };
    }, [ map, isTimelineMode ]);

    if (!map || !isTimelineMode) return null;
    if (!meta) {
        return (
            <MapTimeline
                domain={{ type: 'continuous', values: [ 0, 1 ], step: 1, format: () => '' }}
                cursor={0}
            />
        );
    }

    if (simAccidentsPeriodMode === 'range') {
        // Весь доступный период (все годы) — {n: номер дня, c: число инцидентов}.
        const allEvents = meta.dates
            .map(x => ({ n: dayNum(x.d), c: Number(x.c) || 0 }))
            .sort((a, b) => a.n - b.n);
        if (!allEvents.length) {
            return (
                <MapTimeline
                    domain={{ type: 'continuous', values: [ 0, 1 ], step: 1, format: () => '' }}
                    cursor={0}
                />
            );
        }
        const rMinDay = allEvents[0].n;
        const rMaxDay = allEvents[allEvents.length - 1].n;

        let fromDay = dayNum(simAccidentsRangeFrom);
        let toDay = dayNum(simAccidentsRangeTo);
        if (fromDay === null || fromDay < rMinDay || fromDay > rMaxDay) fromDay = rMinDay;
        if (toDay === null || toDay < rMinDay || toDay > rMaxDay) toDay = rMaxDay;
        if (fromDay > toDay) fromDay = toDay;

        const rDomain = { type: 'continuous', values: [ rMinDay, rMaxDay ], step: 1, format: n => fmtDDMM(isoFromDay(Math.round(n))) };
        const rCounts = allEvents.map(x => x.c);
        const rMinC = Math.min(...rCounts);
        const rMaxC = Math.max(...rCounts);
        const rTickHeight = c => (rMaxC === rMinC
            ? TICK_BASE_PX
            : TICK_MIN_PX + ((c - rMinC) / (rMaxC - rMinC)) * (TICK_MAX_PX - TICK_MIN_PX));
        const rEventTicks = allEvents.map(({ n, c }) => ({
            t: n,
            colors: [ TICK_COLOR ],
            label: `${fmtDDMM(isoFromDay(n))} — ${c}`,
            height: rTickHeight(c),
        }));
        const onRange = ([ a, b ]) => {
            const fromIso = isoFromDay(Math.round(a));
            const toIso = isoFromDay(Math.round(b));
            if (fromIso !== simAccidentsRangeFrom || toIso !== simAccidentsRangeTo) {
                setFilterMulti({ simAccidentsRangeFrom: fromIso, simAccidentsRangeTo: toIso });
            }
        };

        return (
            <MapTimeline
                domain={rDomain}
                range={[ fromDay, toDay ]}
                onRangeChange={onRange}
                eventTicks={rEventTicks}
            />
        );
    }

    // Доступные годы и дни инцидентов выбранного года.
    const years = Array.from(new Set(meta.dates.map(x => Number(x.d.slice(0, 4))))).sort((a, b) => a - b);
    const year = years.includes(simAccidentsYear) ? simAccidentsYear : years[years.length - 1];
    // События выбранного года: {n: номер дня, c: число инцидентов}.
    const yearEvents = meta.dates
        .filter(x => x.d.startsWith(String(year)))
        .map(x => ({ n: dayNum(x.d), c: Number(x.c) || 0 }))
        .sort((a, b) => a.n - b.n);
    const yearDays = yearEvents.map(x => x.n);
    if (!yearDays.length) {
        return (
            <MapTimeline
                domain={{ type: 'continuous', values: [ 0, 1 ], step: 1, format: () => '' }}
                cursor={0}
            />
        );
    }
    const minDay = yearDays[0];
    const maxDay = yearDays[yearDays.length - 1];

    // Текущая дата-курсор: выбранная (если в пределах года) либо последний день года.
    let curDay = dayNum(simAccidentsDate);
    if (curDay === null || curDay < minDay || curDay > maxDay) curDay = maxDay;

    // Степпер года: переход меняет год и ставит дату на последний день нового года.
    const yi = years.indexOf(year);
    const goYear = delta => {
        const ny = years[yi + delta];
        if (!Number.isFinite(ny)) return;
        const ndays = meta.dates.filter(x => x.d.startsWith(String(ny))).map(x => x.d).sort();
        setFilter('simAccidentsYear', ny);
        if (ndays.length) setFilter('simAccidentsDate', ndays[ndays.length - 1]);
    };
    const domain = { type: 'continuous', values: [ minDay, maxDay ], step: 1, format: n => fmtDDMM(isoFromDay(Math.round(n))) };
    // Высота штриха по числу инцидентов: линейно от TICK_MIN_PX (минимум в году)
    // до TICK_MAX_PX (максимум). Если все даты с одинаковым числом — базовая высота.
    const counts = yearEvents.map(x => x.c);
    const minC = Math.min(...counts);
    const maxC = Math.max(...counts);
    const tickHeight = c => (maxC === minC
        ? TICK_BASE_PX
        : TICK_MIN_PX + ((c - minC) / (maxC - minC)) * (TICK_MAX_PX - TICK_MIN_PX));
    const eventTicks = yearEvents.map(({ n, c }) => ({
        t: n,
        colors: [ TICK_COLOR ],
        label: `${fmtDDMM(isoFromDay(n))} — ${c}`,
        height: tickHeight(c),
    }));
    const onCursor = n => {
        const iso = isoFromDay(Math.round(n));
        if (iso !== simAccidentsDate) setFilter('simAccidentsDate', iso);
    };

    return (
        <MapTimeline
            domain={domain}
            cursor={curDay}
            onCursorChange={onCursor}
            eventTicks={eventTicks}
            stepperControl={{
                display: String(year),
                onPrev: () => goYear(-1),
                onNext: () => goYear(1),
                prevDisabled: yi <= 0,
                nextDisabled: yi >= years.length - 1,
            }}
        />
    );
};
const mapStateToProps = ({ mapFilters: {
    simAccidentsPeriodMode, simAccidentsDate, simAccidentsYear,
    simAccidentsRangeFrom, simAccidentsRangeTo,
} }) => ({
    simAccidentsPeriodMode, simAccidentsDate, simAccidentsYear,
    simAccidentsRangeFrom, simAccidentsRangeTo,
});
const mapDispatchToProps = dispatch => ({
    setFilter: (key, value) => dispatch(onChangeMapFilters(key, value)),
    setFilterMulti: payload => dispatch(onChangeMapFiltersMulti(payload)),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(SimAccidentsTimelineComponent);

export const SimAccidentsTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    t`ДТП СИМ`
);
